import React, { useState, ReactNode, useContext } from "react";
import { useAppText, UseAppTextReturn } from "src/commun/composants/AppText";
import { getInitialListeEtapes } from "src/pages/finalisation/hooks/useInitEtapesFinalisation";
import { AppEtape } from "src/commun/composants/formNavigation/AppEtapes";
import { UserToFinalize } from "src/domain/UserToFinalize";

export type FinalisationState = {
  etapes: AppEtape[];
  shortId: string | null;
  userToFinalize: UserToFinalize;
};

function getInitialState(shortId: string | null, userToFinalize: UserToFinalize, appText: UseAppTextReturn): FinalisationState {
  return {
    etapes: getInitialListeEtapes(appText, userToFinalize),
    shortId: shortId,
    userToFinalize: userToFinalize,
  };
}

function useFinalisationContextValue(shortId: string | null, userToFinalize: UserToFinalize) {
  const appText = useAppText();

  const [state, setState] = useState<FinalisationState>(() => getInitialState(shortId, userToFinalize, appText));

  const value = { ...state };

  const api = {
    setEtapes: function(etapes: AppEtape[]) {
      setState(s => ({ ...s, etapes }));
    },
  };

  return [value, api] as const;
}

// Infer return value type of useFinalisationContextValue
export type FinalisationContextValue = ReturnType<typeof useFinalisationContextValue>;
export type FinalisationValue = FinalisationContextValue[0];
export type FinalisationAPI = FinalisationContextValue[1];

const Context = React.createContext<FinalisationContextValue | null>(null);

type FinalisationProviderProps = {
  children: ReactNode;
  shortId: string | null;
  userToFinalize: UserToFinalize;
};
export function FinalisationProvider({ children, shortId, userToFinalize }: FinalisationProviderProps) {
  const value = useFinalisationContextValue( shortId, userToFinalize );
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useFinalisation() {
  const value = useContext(Context);
  if (!value) {
    throw new Error("cant read context because FinalisationProvider was not used!");
  }
  return value;
}
